import { Divider } from '@chakra-ui/react';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import BeneficiaryInviteeButton from './components/BeneficiaryInviteeButton';
import { useHasAlreadyEiAccount } from './hooks';
import locales from './locales';

interface ProfileHelperProps {
  onSelectBeneficiary: () => void;
  onSelectCapitalDeposit: () => void;
  onSelectCompanyCreation: () => void;
  onSelectShineStart: () => void;
  onSelectStandard: () => void;
}

const ProfileHelper = asBentoPage<ProfileHelperProps>(
  ({
    Layout,
    onSelectBeneficiary,
    onSelectCapitalDeposit,
    onSelectCompanyCreation,
    onSelectShineStart,
    onSelectStandard,
  }) => {
    const hasAlreadyEiAccount = useHasAlreadyEiAccount();

    return (
      <Layout data-testid="profile-helper" nav={<OBNavigation />}>
        <Header description={locales.headerDescription} title={locales.title} />

        <Text marginBottom="space-8" variant="light">
          {locales.proAccountSectionHeader}
        </Text>

        <SunshineCard
          data-testid="profile-helper-pro-account"
          isBoxed={false}
          onClick={onSelectStandard}
          paddingY="space-16"
          title={locales.openProAccountTitle}
        >
          <SunshineCard.Slot name="aside">
            <SquircleIcon name="company" />
          </SunshineCard.Slot>

          <SunshineCard.Content>
            <SunshineCard.Text>
              {locales.openProAccountDescription}
            </SunshineCard.Text>
          </SunshineCard.Content>

          <SunshineCard.Slot name="action">
            <SunshineCard.SunshineIcon name="chevron-right" />
          </SunshineCard.Slot>
        </SunshineCard>

        <Divider borderColor="grey.300" marginY="space-24" />

        <Text marginBottom="space-8" variant="light">
          {locales.companyCreationSectionHeader}
        </Text>

        <SunshineCard
          data-testid="profile-helper-shine-start"
          isBoxed={false}
          isDisabled={hasAlreadyEiAccount}
          onClick={onSelectShineStart}
          paddingY="space-16"
          title={locales.shineStartTitle}
        >
          <SunshineCard.Slot name="aside">
            <SquircleIcon name="flash" />
          </SunshineCard.Slot>

          <SunshineCard.Content>
            <SunshineCard.Text>
              {hasAlreadyEiAccount
                ? locales.onlyOneEI
                : locales.shineStartDescription}
            </SunshineCard.Text>
          </SunshineCard.Content>

          {hasAlreadyEiAccount ? undefined : (
            <SunshineCard.Slot name="action">
              <SunshineCard.SunshineIcon name="chevron-right" />
            </SunshineCard.Slot>
          )}
        </SunshineCard>

        <SunshineCard
          isBoxed={false}
          onClick={onSelectCompanyCreation}
          paddingY="space-16"
          title={locales.createCompanyTitle}
        >
          <SunshineCard.Slot name="aside">
            <SquircleIcon name="toolbox" />
          </SunshineCard.Slot>

          <SunshineCard.Content>
            <SunshineCard.Text>
              {locales.createCompanyDescription}
            </SunshineCard.Text>
          </SunshineCard.Content>

          <SunshineCard.Slot name="action">
            <SunshineCard.SunshineIcon name="chevron-right" />
          </SunshineCard.Slot>
        </SunshineCard>

        <Divider borderColor="grey.300" marginY="space-24" />

        <Text marginBottom="space-8" variant="light">
          {locales.companyUnderRegistrationSectionHeader}
        </Text>

        <SunshineCard
          data-testid="profile-helper-capital-deposit"
          isBoxed={false}
          marginBottom="space-32"
          onClick={onSelectCapitalDeposit}
          paddingY="space-16"
          title={locales.capitalDepositTitle}
        >
          <SunshineCard.Slot name="aside">
            <SquircleIcon name="file" />
          </SunshineCard.Slot>

          <SunshineCard.Content>
            <SunshineCard.Text>
              {locales.capitalDepositDescription}
            </SunshineCard.Text>
          </SunshineCard.Content>

          <SunshineCard.Slot name="action">
            <SunshineCard.SunshineIcon name="chevron-right" />
          </SunshineCard.Slot>
        </SunshineCard>

        <BeneficiaryInviteeButton onClick={onSelectBeneficiary} />
      </Layout>
    );
  },
);

export default ProfileHelper;
